import { z } from 'zod';
import { NOTE_ASSIGNMENT_STATUSES, QUICK_TASK_CATEGORIES } from '../enums';
import { isoDate, isoDateTime, richText, uuid } from './common';

export const createNoteSchema = z.object({
  title: z.string().min(1).max(200),
  content: richText.optional(),
  folder: z.string().max(80).nullable().optional(),
  tags: z.array(z.string().min(1).max(40)).max(20).optional(),
  pinned: z.boolean().optional(),
  template: z.enum(['blank', 'meeting', 'weekly_plan']).optional(),
});
export type CreateNoteInput = z.infer<typeof createNoteSchema>;

export const updateNoteSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  content: richText.optional(),
  folder: z.string().max(80).nullable().optional(),
  tags: z.array(z.string().min(1).max(40)).max(20).optional(),
  pinned: z.boolean().optional(),
  version: z.number().int().min(1).optional(),
});
export type UpdateNoteInput = z.infer<typeof updateNoteSchema>;

/** Assign a whole note or selected blocks (FR-NOTE-6). */
export const assignNoteSchema = z.object({
  assigneeId: uuid,
  blockIds: z.array(z.string()).optional(),
  instructions: z.string().max(4000).optional(),
  dueDate: isoDate.optional(),
  canEdit: z.boolean().default(false),
});
export type AssignNoteInput = z.infer<typeof assignNoteSchema>;

export const updateNoteAssignmentSchema = z.object({
  status: z.enum(NOTE_ASSIGNMENT_STATUSES).optional(),
  reply: z.string().min(1).max(4000).optional(),
});
export type UpdateNoteAssignmentInput = z.infer<typeof updateNoteAssignmentSchema>;

/** Convert an assignment to a Quick Task (FR-NOTE-9). */
export const convertAssignmentSchema = z.object({
  projectKey: z.string(),
  estimateMin: z.number().int().min(1).optional(),
  category: z.enum(QUICK_TASK_CATEGORIES).default('internal'),
});
export type ConvertAssignmentInput = z.infer<typeof convertAssignmentSchema>;

/** Create one or many work items from note blocks (FR-NOTE-10, FR-NOTE-13). */
export const createItemsFromNoteSchema = z.object({
  items: z
    .array(
      z.object({
        blockId: z.string().min(1),
        projectKey: z.string(),
        type: z.string().min(1),
        title: z.string().min(1).max(255),
        description: z.string().max(10000).optional(),
        releaseId: uuid.nullable().optional(),
        assigneeId: uuid.nullable().optional(),
        estimateMin: z.number().int().min(1).optional(),
        dueDate: isoDate.optional(),
      }),
    )
    .min(1)
    .max(100),
});
export type CreateItemsFromNoteInput = z.infer<typeof createItemsFromNoteSchema>;

export const noteReminderSchema = z.object({ blockId: z.string().min(1), remindAt: isoDateTime });
